import { useSuspenseQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Button,
  ColDefBuilder,
  DataTable,
  Heading,
  Icon,
  Stack,
  Text,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTenantIdFromParams } from '@eluve/session-helpers';

import { DisableBundleConfirmationDialog } from '../../template-bundles/DisableBundleConfirmationDialog';
import { TenantBundleDefaultsSection } from '../../template-bundles/TenantBundleDefaultsSection';
import {
  getTemplateBundleDisplayName,
  templateBundleListItemFragment,
} from '../../template-bundles/template-bundle.operations';
import { useDisableBundleFlow } from '../../template-bundles/useDisableBundleFlow';

import {
  MigrateDefaultBundleDialog,
  UpdateBundleBySpecialtyDialog,
  UpdateDoctorIdsDialog,
} from './bulk-default-bundle';
import {
  TenantTemplateBundleToggle,
  tenantTemplateBundleFragment,
} from './components/TenantTemplateBundleToggle';

const getTenantTemplateBundlesQuery = graphql(
  `
    query getTenantTemplateBundles($tenantId: uuid!) {
      tenantsByPk(id: $tenantId) {
        __typename
        id
        name
      }
      llmModelPromptTemplates(
        where: {
          isActive: { _eq: true }
          isBundle: { _eq: true }
          output_template: { tenantId: { _isNull: true } }
        }
        orderBy: { displayName: ASC }
      ) {
        ...TemplateBundleListItem
        tenant_template_bundles(where: { tenantId: { _eq: $tenantId } }) {
          ...TenantTemplateBundle
        }
      }
      ownBundles: llmModelPromptTemplates(
        where: {
          isBundle: { _eq: true }
          output_template: { tenantId: { _eq: $tenantId } }
        }
        orderBy: { displayName: ASC }
      ) {
        ...TemplateBundleListItem
        tenant_template_bundles(where: { tenantId: { _eq: $tenantId } }) {
          ...TenantTemplateBundle
        }
      }
    }
  `,
  [tenantTemplateBundleFragment, templateBundleListItemFragment],
);

type BundleRow = {
  llmModelPromptTemplateId: string;
  tenantId: string;
  promptTemplateName: string;
  outputTemplateName: string;
  modelName: string;
  isEnabled: boolean;
  updatedAt: string;
  displayName: string;
};

const buildBundleColumns = (onRequestDisable: (row: BundleRow) => void) =>
  new ColDefBuilder<BundleRow>()
    .linkSortable(
      'displayName',
      (row) => `/admin/template-bundles/${row.llmModelPromptTemplateId}`,
      'Display Name',
    )
    .defaultSortable('promptTemplateName', 'Prompt Template')
    .defaultSortable('outputTemplateName', 'Output Template')
    .defaultSortable('modelName', 'Model')
    .defaultSortable('isEnabled', {
      label: 'Is Enabled for this Org',
      cellRenderer: (row) => {
        return (
          <TenantTemplateBundleToggle
            tenantId={row.tenantId}
            llmModelPromptTemplateId={row.llmModelPromptTemplateId}
            displayName={row.displayName}
            onRequestDisable={() => onRequestDisable(row)}
          />
        );
      },
    })
    .dateSortable('updatedAt', 'Updated At')
    .build();

// Columns for bundles OWNED by this org (created in the tenant editor). The
// name links to the tenant-scoped detail page rather than the global one.
const buildOwnBundleColumns = (
  onRequestDisable: (row: BundleRow) => void,
  tenantId: string,
) =>
  new ColDefBuilder<BundleRow>()
    .linkSortable(
      'displayName',
      (row) =>
        `/admin/organizations/${tenantId}/manage/template-bundles/${row.llmModelPromptTemplateId}`,
      'Display Name',
    )
    .defaultSortable('promptTemplateName', 'Prompt Template')
    .defaultSortable('outputTemplateName', 'Output Template')
    .defaultSortable('modelName', 'Model')
    .defaultSortable('isEnabled', {
      label: 'Is Enabled for this Org',
      cellRenderer: (row) => (
        <TenantTemplateBundleToggle
          tenantId={row.tenantId}
          llmModelPromptTemplateId={row.llmModelPromptTemplateId}
          displayName={row.displayName}
          onRequestDisable={() => onRequestDisable(row)}
        />
      ),
    })
    .dateSortable('updatedAt', 'Updated At')
    .build();

export const AdminTenantTemplateBundlesPage: React.FC = () => {
  const tenantId = useTenantIdFromParams()!;
  const [isMigrateBundleDialogOpen, setIsMigrateBundleDialogOpen] =
    useState(false);
  const [isUpdateBySpecialtyDialogOpen, setIsUpdateBySpecialtyDialogOpen] =
    useState(false);
  const [isUpdateDoctorIdsDialogOpen, setIsUpdateDoctorIdsDialogOpen] =
    useState(false);

  const { requestDisable, dialogProps } = useDisableBundleFlow({
    mode: 'tenant',
    tenantId,
  });

  const handleRequestDisable = useMemo(
    () => (row: BundleRow) => {
      requestDisable(row.llmModelPromptTemplateId, row.displayName);
    },
    [requestDisable],
  );

  const bundleColumns = useMemo(
    () => buildBundleColumns(handleRequestDisable),
    [handleRequestDisable],
  );

  const ownBundleColumns = useMemo(
    () => buildOwnBundleColumns(handleRequestDisable, tenantId),
    [handleRequestDisable, tenantId],
  );

  const {
    data: { llmModelPromptTemplates, tenantsByPk, ownBundles },
  } = useSuspenseQuery(getTenantTemplateBundlesQuery, {
    variables: {
      tenantId,
    },
    context: eluveAdminHasuraContext,
  });

  const tenantName = tenantsByPk?.name ?? '';

  // Only offer bundles that are actually enabled for this tenant so the admin
  // can't assign a user a bundle they have no access to.
  const enabledBundles = useMemo(
    () =>
      llmModelPromptTemplates.filter(
        (b) => b.tenant_template_bundles?.[0]?.isEnabled,
      ),
    [llmModelPromptTemplates],
  );

  const rows = useMemo(() => {
    return llmModelPromptTemplates.map<BundleRow>((bundle) => {
      const promptName = bundle.template?.name ?? 'Unknown Prompt';
      const outputName = bundle.output_template?.name ?? 'None';
      const modelName =
        bundle.model_args?.name ??
        bundle.model_args?.modelType ??
        'Unknown Model';
      const tenantBundle = bundle.tenant_template_bundles?.[0];

      return {
        llmModelPromptTemplateId: bundle.id,
        tenantId,
        promptTemplateName: promptName,
        outputTemplateName: outputName,
        modelName,
        isEnabled: tenantBundle?.isEnabled ?? false,
        updatedAt: tenantBundle?.updatedAt ?? '',
        displayName: getTemplateBundleDisplayName(bundle),
      };
    });
  }, [llmModelPromptTemplates, tenantId]);

  const ownRows = useMemo(() => {
    return ownBundles.map<BundleRow>((bundle) => {
      const promptName = bundle.template?.name ?? 'Unknown Prompt';
      const outputName = bundle.output_template?.name ?? 'None';
      const modelName =
        bundle.model_args?.name ??
        bundle.model_args?.modelType ??
        'Unknown Model';
      const tenantBundle = bundle.tenant_template_bundles?.[0];

      return {
        llmModelPromptTemplateId: bundle.id,
        tenantId,
        promptTemplateName: promptName,
        outputTemplateName: outputName,
        modelName,
        isEnabled: tenantBundle?.isEnabled ?? false,
        updatedAt: tenantBundle?.updatedAt ?? '',
        displayName: getTemplateBundleDisplayName(bundle),
      };
    });
  }, [ownBundles, tenantId]);

  return (
    <Stack className="grid" gap={2}>
      <Stack align="start" gap={2} direction="horizontal" justify="between">
        <Stack gap={2}>
          <Heading size="2xl">Template Bundles</Heading>
          <Text>
            Control which template bundles (prompt + output template + model)
            this organization will have access to
          </Text>
        </Stack>
        <Stack direction="horizontal" gap={2}>
          <Button
            variant="outline"
            onClick={() => setIsMigrateBundleDialogOpen(true)}
          >
            <Icon name="ArrowRightLeft" size="auto" />
            Migrate Default Bundle
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsUpdateBySpecialtyDialogOpen(true)}
          >
            <Icon name="Stethoscope" size="auto" />
            Update by Specialty
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsUpdateDoctorIdsDialogOpen(true)}
          >
            <Icon name="List" size="auto" />
            Update Doctor IDs
          </Button>
        </Stack>
      </Stack>
      <DataTable
        data={rows}
        columns={bundleColumns}
        isPaginated={false}
        enableGlobalSearch
        initialSortingState={[
          {
            id: 'isEnabled',
            desc: true,
          },
        ]}
      />

      <DisableBundleConfirmationDialog {...dialogProps} />

      <MigrateDefaultBundleDialog
        tenantId={tenantId}
        tenantName={tenantName}
        bundles={enabledBundles}
        open={isMigrateBundleDialogOpen}
        onOpenChange={setIsMigrateBundleDialogOpen}
      />

      <UpdateBundleBySpecialtyDialog
        tenantId={tenantId}
        tenantName={tenantName}
        bundles={enabledBundles}
        open={isUpdateBySpecialtyDialogOpen}
        onOpenChange={setIsUpdateBySpecialtyDialogOpen}
      />

      <UpdateDoctorIdsDialog
        tenantId={tenantId}
        tenantName={tenantName}
        bundles={enabledBundles}
        open={isUpdateDoctorIdsDialogOpen}
        onOpenChange={setIsUpdateDoctorIdsDialogOpen}
      />

      <div className="mt-8">
        <Stack gap={2}>
          <Heading size="xl">This organization's templates</Heading>
          <Text>
            Template bundles this organization created in the template editor.
            They're owned by the organization — open one to view its versions
            and model.
          </Text>
          {ownRows.length === 0 ? (
            <Text color="tertiary">
              No organization-specific templates yet.
            </Text>
          ) : (
            <DataTable
              data={ownRows}
              columns={ownBundleColumns}
              isPaginated={false}
              enableGlobalSearch
            />
          )}
        </Stack>
      </div>

      <div className="mt-8">
        <TenantBundleDefaultsSection
          tenantId={tenantId}
          tenantName={tenantName}
          enabledBundles={enabledBundles}
        />
      </div>
    </Stack>
  );
};
